/**
 * Create Task Drawer Component
 * 
 * Slide drawer for creating new tasks.
 * Supports both Demo mode (hardcoded data) and Live mode (API-driven data
 * passed in via props).
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TaskType } from '@/types/tasks';

// ---------------------------------------------------------------------------
// Validation constants (aligned with the backend / database schema)
// ---------------------------------------------------------------------------
const MAX_TITLE_LENGTH = 150;
const MAX_DESCRIPTION_LENGTH = 2000;

// ---------------------------------------------------------------------------
// Demo-mode hardcoded data (kept intact so demo behaviour is unchanged)
// ---------------------------------------------------------------------------

/** Demo-only list of users with avatars */
const DEMO_USERS: CreateTaskUser[] = [
  { id: '1', name: 'Sarah Johnson', avatar: 'https://i.pravatar.cc/150?img=1' },
  { id: '2', name: 'Michael Chen', avatar: 'https://i.pravatar.cc/150?img=13' },
  { id: '3', name: 'Emily Rodriguez', avatar: 'https://i.pravatar.cc/150?img=5' },
  { id: '4', name: 'David Kim', avatar: 'https://i.pravatar.cc/150?img=12' },
  { id: '5', name: 'Jessica Williams', avatar: 'https://i.pravatar.cc/150?img=9' },
  { id: '6', name: 'Robert Martinez', avatar: 'https://i.pravatar.cc/150?img=15' },
  { id: '7', name: 'Amanda Taylor', avatar: 'https://i.pravatar.cc/150?img=10' },
  { id: '8', name: 'James Anderson', avatar: 'https://i.pravatar.cc/150?img=14' },
];

/** Demo-only list of customers with account numbers */
const DEMO_CUSTOMERS: CreateTaskCustomer[] = [
  { id: '1', accountNumber: 'ACC-10001', name: 'Acme Corporation' },
  { id: '2', accountNumber: 'ACC-10002', name: 'Global Tech Solutions' },
  { id: '3', accountNumber: 'ACC-10003', name: 'Sunrise Industries Ltd' },
  { id: '4', accountNumber: 'ACC-10004', name: 'Blue Ocean Enterprises' },
  { id: '5', accountNumber: 'ACC-10005', name: 'Metro Manufacturing Co' },
  { id: '6', accountNumber: 'ACC-10006', name: 'Pacific Retail Group' },
  { id: '7', accountNumber: 'ACC-10007', name: 'Sterling Financial Services' },
  { id: '8', accountNumber: 'ACC-10008', name: 'Quantum Dynamics Inc' },
  { id: '9', accountNumber: 'ACC-10009', name: 'Horizon Healthcare Systems' },
  { id: '10', accountNumber: 'ACC-10010', name: 'Evergreen Logistics' },
  { id: '11', accountNumber: 'ACC-10011', name: 'Summit Construction Group' },
  { id: '12', accountNumber: 'ACC-10012', name: 'Nexus Technology Partners' },
  { id: '13', accountNumber: 'ACC-10013', name: 'Coastal Trading Company' },
  { id: '14', accountNumber: 'ACC-10014', name: 'Alpine Energy Solutions' },
  { id: '15', accountNumber: 'ACC-10015', name: 'Velocity Transport Services' },
];

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface CreateTaskUser {
  id: string;
  name: string;
  avatar?: string;
}

export interface CreateTaskCustomer {
  id: string;
  accountNumber: string;
  name: string;
}

export interface NewTaskData {
  title: string;
  description: string;
  type: TaskType;
  assignedToId: string;
  assignedToName: string;
  dueDate: Date;
  customerId: string;
  customerName: string;
}

interface CreateTaskDrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreateTask: (task: NewTaskData) => void;
  /** Users to show in the Assigned To dropdown. Falls back to demo data when empty. */
  readonly users?: CreateTaskUser[];
  /** Customers to show in the Customer combobox. Falls back to demo data when empty. */
  readonly customers?: CreateTaskCustomer[];
  /** Whether the mutation is currently in-flight */
  readonly isSubmitting?: boolean;
}

// ---------------------------------------------------------------------------
// Internal form state
// ---------------------------------------------------------------------------
interface FormState {
  title: string;
  description: string;
  assignedToId: string;
  dueDate: Date | undefined;
  customerId: string;
}

const INITIAL_FORM_STATE: FormState = {
  title: '',
  description: '',
  assignedToId: '',
  dueDate: undefined,
  customerId: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateTaskDrawer({
  open,
  onOpenChange,
  onCreateTask,
  users,
  customers,
  isSubmitting = false,
}: CreateTaskDrawerProps) {
  const [formData, setFormData] = useState<FormState>(INITIAL_FORM_STATE);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Resolve which datasets to render
  const resolvedUsers = users && users.length > 0 ? users : DEMO_USERS;
  const resolvedCustomers = customers && customers.length > 0 ? customers : DEMO_CUSTOMERS;

  // Derived helpers
  const selectedUser = resolvedUsers.find((u) => u.id === formData.assignedToId);
  const selectedCustomer = resolvedCustomers.find((c) => c.id === formData.customerId);

  // ------ Validation ------
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required.';
    } else if (formData.title.trim().length > MAX_TITLE_LENGTH) {
      errors.title = `Title must not exceed ${MAX_TITLE_LENGTH} characters.`;
    }

    if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
      errors.description = `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
    }

    if (!formData.assignedToId) {
      errors.assignedToId = 'Assigned To is required.';
    }

    if (!formData.dueDate) {
      errors.dueDate = 'Due Date is required.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ------ Handlers ------
  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setValidationErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!formData.dueDate) return;

    const user = resolvedUsers.find((u) => u.id === formData.assignedToId);
    const customer = resolvedCustomers.find((c) => c.id === formData.customerId);

    const taskData: NewTaskData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      type: 'task',
      assignedToId: formData.assignedToId,
      assignedToName: user?.name ?? '',
      dueDate: formData.dueDate,
      customerId: formData.customerId,
      customerName: customer?.name ?? '',
    };

    onCreateTask(taskData);
    resetForm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  // ------ Render ------
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto px-8">
        <form onSubmit={handleSubmit}>
          <SheetHeader>
            <SheetTitle>Create New Task</SheetTitle>
            <SheetDescription>
              Add a new task to track collection activities
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-4 py-6">
            {/* Task Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Task Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Enter task title"
                value={formData.title}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (validationErrors.title) {
                    setValidationErrors((prev) => {
                      const next = { ...prev };
                      delete next.title;
                      return next;
                    });
                  }
                }}
                required
              />
              <div className="flex justify-between">
                {validationErrors.title && (
                  <p className="text-xs text-red-500">{validationErrors.title}</p>
                )}
                <p className={cn(
                  "text-xs text-muted-foreground ml-auto",
                  formData.title.length > MAX_TITLE_LENGTH && "text-red-500"
                )}>
                  {formData.title.length}/{MAX_TITLE_LENGTH}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Description
              </Label>
              <Textarea
                id="description"
                placeholder="Enter task description"
                value={formData.description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (validationErrors.description) {
                    setValidationErrors((prev) => {
                      const next = { ...prev };
                      delete next.description;
                      return next;
                    });
                  }
                }}
                rows={4}
              />
              <div className="flex justify-between">
                {validationErrors.description && (
                  <p className="text-xs text-red-500">{validationErrors.description}</p>
                )}
                <p className={cn(
                  "text-xs text-muted-foreground ml-auto",
                  formData.description.length > MAX_DESCRIPTION_LENGTH && "text-red-500"
                )}>
                  {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
                </p>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-2 gap-4">
              {/* Row 1: Task Type (Hardcoded) */}
              <div className="space-y-2">
                <Label htmlFor="type">
                  Task Type <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="type"
                  value="Task"
                  disabled
                  className="bg-muted"
                />
              </div>

              {/* Row 1: Assigned To */}
              <div className="space-y-2">
                <Label htmlFor="assignedTo">
                  Assigned To <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.assignedToId}
                  onValueChange={(value) => {
                    setFormData({ ...formData, assignedToId: value });
                    if (validationErrors.assignedToId) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.assignedToId;
                        return next;
                      });
                    }
                  }}
                >
                  <SelectTrigger id="assignedTo">
                    <SelectValue placeholder="Select assignee">
                      {selectedUser && (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage
                              src={selectedUser.avatar}
                              alt={selectedUser.name}
                            />
                            <AvatarFallback>
                              {selectedUser.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <span>{selectedUser.name}</span>
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {resolvedUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={user.avatar} alt={user.name} />
                            <AvatarFallback>
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.assignedToId && (
                  <p className="text-xs text-red-500">{validationErrors.assignedToId}</p>
                )}
              </div>

              {/* Row 2: Due Date */}
              <div className="col-span-2 sm:col-span-1 space-y-2">
                <Label htmlFor="dueDate">
                  Due Date <span className="text-red-500">*</span>
                </Label>
                <DatePicker
                  date={formData.dueDate}
                  onDateChange={(date) => {
                    setFormData({ ...formData, dueDate: date });
                    if (validationErrors.dueDate) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.dueDate;
                        return next;
                      });
                    }
                  }}
                  placeholder="Select due date"
                />
                {validationErrors.dueDate && (
                  <p className="text-xs text-red-500">{validationErrors.dueDate}</p>
                )}
              </div>

              {/* Customer - Full Width */}
              <div className="col-span-2 space-y-2">
                <Label htmlFor="customerName">
                  Customer
                </Label>
                <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="customerName"
                      variant="outline"
                      aria-expanded={customerOpen}
                      aria-haspopup="listbox"
                      className="w-full justify-between"
                    >
                      {selectedCustomer ? (
                        <span className="truncate">
                          {selectedCustomer.accountNumber} \u2022 {selectedCustomer.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select customer...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search customer..." />
                      <CommandList>
                        <CommandEmpty>No customer found.</CommandEmpty>
                        <CommandGroup>
                          {resolvedCustomers.map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={`${customer.accountNumber} ${customer.name}`}
                              onSelect={() => {
                                setFormData({ ...formData, customerId: customer.id });
                                setCustomerOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.customerId === customer.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="text-xs text-muted-foreground">
                                  {customer.accountNumber}
                                </span>
                                <span>{customer.name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

            </div>
          </div>

          <SheetFooter className="flex-row gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Task'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
